import hmac_sha256 from 'crypto-js/hmac-sha256';

import type { BusinessMessage } from './type';
import type { MessageContent, TopSportCountData, LeftSportCountData } from './proto/notification';
import { EventEnum } from './type';
import { getDeviceId } from './deviceId';

/** 与网关约定：用户维度 VIP 推送的订阅 topic 前缀，完整格式为 `BZ:VIP:{userId}` */
export const BZ_VIP_TOPIC_PREFIX = 'VIP:' as const;

/**
 * 生成 VIP 用户维度 WebSocket 订阅 topic（例：`BZ:VIP:10001`）
 * @param userId 当前登录用户 ID（与站内信等 topic 并列发送至 `subscribe.topics`）
 */
export function makeBzVipUserTopic(userId: string | number): string {
  return `${BZ_VIP_TOPIC_PREFIX}${userId}`;
}

/**
 * 获取 WebSocket URL
 * @returns
 */
export function getSockUrl(): string {
  // 优先使用显式配置，否则回退当前域名
  const host = import.meta.env.DEV ? import.meta.env.VITE_DEV_HOST : window.location.host;

  return `wss://ws.${host}/api/notification/websocket`;
}

/**
 * 生成 WebSocket 身份验证参数
 * @returns
 */
export const makeSocketIdentity = () => {
  const tempTimestamp = Date.now().toString(36).slice(-4);
  const deviceId = `${getDeviceId()}.${tempTimestamp}`;
  const timestamp = Date.now();
  const hash = hmac_sha256(deviceId, timestamp.toString());
  return `deviceId=${encodeURIComponent(deviceId)}&t=${timestamp}&p=${hash}&proto=protobuf`;
};

/**
 * 根据 MessageContent type 类型生成 WS 消费数据
 * oddsData 赔率数据按比赛纬度推送，一场比赛有多个赔率数据，所以 oddsData 是数组
 * 所以统一将其他类型的推送数据也整理成数组
 * @param data
 * @returns
 */
export const generateWSData = (data: MessageContent): BusinessMessage[] | undefined => {
  switch (data.type) {
    case EventEnum.match:
      return [
        {
          type: EventEnum.match,
          data: data.matchData!,
          timestamp: Date.now(),
          matchId: data.matchId,
        },
      ];

    case EventEnum.odds:
      return (
        data.oddsData?.data?.map((item) => {
          return {
            type: EventEnum.odds,
            data: item,
            timestamp: Date.now(),
            code: item.code,
            matchId: data.matchId,
            marketId: item.marketId,
            specifiers: item.specifiers,
          };
        }) ?? []
      );

    case EventEnum.odds_recommend:
      return [
        {
          type: EventEnum.odds_recommend,
          data: data.oddsRecommendData!,
          timestamp: Date.now(),
        },
      ];

    case EventEnum.top_sport_count:
      return [
        {
          type: EventEnum.top_sport_count,
          data: (data as unknown as { data: TopSportCountData }).data,
          timestamp: Date.now(),
        },
      ];

    case EventEnum.left_sport_count:
      return [
        {
          type: EventEnum.left_sport_count,
          data: (data as unknown as { data: LeftSportCountData }).data,
          timestamp: Date.now(),
        },
      ];

    case EventEnum.notification_msg_route:
      return [
        {
          type: EventEnum.notification_msg_route,
          data: data,
          timestamp: Date.now(),
        },
      ];

    case EventEnum.balance_change_event:
      return [
        {
          type: EventEnum.balance_change_event,
          data: data,
          timestamp: Date.now(),
        },
      ];

    case EventEnum.bz_vip:
      return [
        {
          type: EventEnum.bz_vip,
          data: data as unknown as Record<string, unknown>,
          timestamp: Date.now(),
        },
      ];
    case EventEnum.odds_handover:
      return [
        {
          type: EventEnum.odds_handover,
          data: null,
          timestamp: Date.now(),
          matchId: data.matchId,
        },
      ];
  }
};

/**
 * 将网关下行（protobuf 解出的对象或 JSON）统一成 `BusinessMessage[]`。
 * - 先走 {@link generateWSData}（依赖 `MessageContent.type`）。
 * - 若无匹配分支：识别 **VIP 进度类裸对象**（常见为订阅 `BZ:VIP:userId` 后直推无 `type` 的 JSON），映射为 {@link EventEnum.bz_vip}，供 `useBzVipPush` 消费。
 */
export function normalizeInboundWsPayload(raw: unknown): BusinessMessage[] | undefined {
  if (raw == null || typeof raw !== 'object') return undefined;

  const fromEnum = generateWSData(raw as MessageContent);
  if (fromEnum != null && fromEnum.length > 0) return fromEnum;

  const o = raw as Record<string, unknown>;
  const uid = o.userId;
  const hasUid = typeof uid === 'number' || typeof uid === 'string';
  const fromLv = o.fromLevel;
  const toLv = o.toLevel;
  const hasLevelPair =
    (typeof fromLv === 'number' || typeof fromLv === 'string') &&
    (typeof toLv === 'number' || typeof toLv === 'string');

  if (hasUid && hasLevelPair) {
    return [
      {
        type: EventEnum.bz_vip,
        data: o,
        timestamp: Date.now(),
      },
    ];
  }

  return undefined;
}
